import { Button, IconButton, ToggleButton, ToggleButtonGroup } from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import Columns from "../../../library/components/layout/Columns";
import Rows from "../../../library/components/layout/Rows";
import { appIcons } from "../../../app/appIcons";
import { PDF_ANNOTATION_COLOR_GROUPS } from "./pdfCanvasConstants";
import PdfCanvasZoomControls from "./PdfCanvasZoomControls";
import type { PdfCanvasBrushSizeType, PdfCanvasEraserCursorState, PdfCanvasToolType } from "./pdfCanvasTypes";

interface IPdfCanvasToolbarProps {
    variant: "wide-sidebar" | "stacked",
    pdfWidth: number,
    onPdfWidthChange: (width: number) => void,
    readOnly: boolean,
    eskiYontem: boolean,
    isWideLayout: boolean,
    isLegendVisible: boolean,
    onToggleLegend: () => void,
    isHataNotPanelVisible: boolean,
    onToggleHataNotPanel: () => void,
    isPreviewVisible: boolean,
    onTogglePreview: () => void,
    canUndo: () => boolean,
    canRedo: () => boolean,
    onUndo: () => void,
    onRedo: () => void,
    seciliRenk: string,
    seciliArac: PdfCanvasToolType,
    onSeciliRenkChange: (renk: string) => void,
    onSeciliAracChange: (arac: PdfCanvasToolType) => void,
    onEraserCursorChange: React.Dispatch<React.SetStateAction<PdfCanvasEraserCursorState>>,
    fircaBoyut: PdfCanvasBrushSizeType,
    onFircaBoyutChange: (boyut: PdfCanvasBrushSizeType) => void,
}

const PdfCanvasToolbar = (props: IPdfCanvasToolbarProps) => {
    const legendToggleButton = props.eskiYontem ? null : <ToggleButton
        value="legend"
        selected={props.isLegendVisible}
        title={props.isLegendVisible ? "Renk açıklamalarını gizle" : "Renk açıklamalarını göster"}
        onChange={props.onToggleLegend}>
        {appIcons.Genel.info}
    </ToggleButton>;

    const hataNotlarToggleButton = props.eskiYontem ? null : <ToggleButton
        value="hataNotlar"
        selected={props.isHataNotPanelVisible}
        title={props.isHataNotPanelVisible ? "Hatalar/Notları gizle" : "Hatalar/Notları göster"}
        onChange={props.onToggleHataNotPanel}>
        {appIcons.Genel.soruList}
    </ToggleButton>;

    const previewToggleButton = <ToggleButton
        value="showPreview"
        selected={props.isPreviewVisible}
        onChange={props.onTogglePreview}>
        {appIcons.Genel.onizleme}
    </ToggleButton>;

    const historyControls = <Columns gap={0}>
        {!props.readOnly ?
            <>
                <IconButton disabled={!props.canUndo()} onClick={props.onUndo}>
                    <UndoIcon />
                </IconButton>
                <IconButton disabled={!props.canRedo()} onClick={props.onRedo}>
                    <RedoIcon />
                </IconButton>
            </>
            : null}
    </Columns>;

    const colorControls = <Columns gap={4} style={{ alignItems: "center" }}>
        {PDF_ANNOTATION_COLOR_GROUPS.map(group =>
            <Columns key={group.groupName} gap={4} mr={10} style={{ alignItems: "center" }}>
                {group.items.map(item =>
                    <Button
                        key={item.color}
                        onClick={() => {
                            if (props.seciliArac === "draw" && props.seciliRenk === item.color) {
                                props.onSeciliAracChange("none");
                                props.onEraserCursorChange(prev => ({ ...prev, visible: false }));
                            }
                            else {
                                props.onSeciliRenkChange(item.color);
                                props.onSeciliAracChange("draw");
                            }
                        }}
                        title={`${group.groupName}: ${item.label}`}
                        style={{
                            minWidth: 24,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: (props.seciliArac === "draw" && props.seciliRenk === item.color) ? "2px solid black" : "1px solid #ccc",
                            background: item.color,
                        }}
                    />
                )}
            </Columns>
        )}
        <Button
            onClick={() => props.onSeciliAracChange("erase")}
            title="Silgi"
            style={{
                minWidth: 24,
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: props.seciliArac === "erase" ? "2px solid black" : "1px solid #ccc",
                background: "white",
                backgroundImage: "linear-gradient(135deg, transparent 42%, rgba(220,0,0,0.9) 45%, rgba(220,0,0,0.9) 55%, transparent 58%)",
            }}
        />
    </Columns>;

    const brushSizeControls = <ToggleButtonGroup
        size="small"
        exclusive
        value={props.fircaBoyut}
        onChange={(_, value) => value && props.onFircaBoyutChange(value)}>
        <ToggleButton value="kucuk">Küçük</ToggleButton>
        <ToggleButton value="orta">Orta</ToggleButton>
        <ToggleButton value="buyuk">Büyük</ToggleButton>
    </ToggleButtonGroup>;

    const zoomControls = <PdfCanvasZoomControls pdfWidth={props.pdfWidth} onPdfWidthChange={props.onPdfWidthChange} />;

    if (props.variant === "wide-sidebar") {
        return <Rows gap={8} style={{ flexShrink: 0 }}>
            <Columns gap={4} style={{ alignItems: "center" }}>
                {zoomControls}
            </Columns>
            <Columns gap={4} style={{ alignItems: "center" }}>
                {legendToggleButton}
                {hataNotlarToggleButton}
                {previewToggleButton}
                {historyControls}
            </Columns>
            {!props.readOnly && <Rows gap={4} style={{ alignItems: "center" }}>
                {colorControls}
                {brushSizeControls}
            </Rows>
            }
        </Rows>;
    }

    return <Columns wrap height={42} gap={30} style={{ alignItems: "center" }}>
        {zoomControls}
        <Columns gap={4}>
            {legendToggleButton}
            {hataNotlarToggleButton}
            {previewToggleButton}
        </Columns>
        {props.readOnly ? null : <>
            {historyControls}
            {colorControls}
            {brushSizeControls}
        </>}
    </Columns>;
};

export default PdfCanvasToolbar;
